import { useId, useRef, useState, type FormEvent, type KeyboardEvent } from "react";
import { TAG_NAME_MAX, type LeadWriteResponse } from "@gioia365/contracts";
import { useQuery } from "@tanstack/react-query";
import { ApiRequestError } from "../../api/http";
import { Button } from "../../design/components/Button";
import { Checkbox } from "../../design/components/Checkbox";
import { Dialog } from "../../design/components/Dialog";
import { TextField } from "../../design/components/TextField";
import { useToast } from "../../design/components/Toast";
import { messageForError } from "../../i18n/errors";
import { useT } from "../../i18n/locale";
import type { TabContext } from "../../session/tab-context";
import { SupersededWriteError, tagsQuery, useCreateTag, type CardLead } from "../lead-queries";
import { newTagName, tagsMatching } from "../new-tag";

// I tag del Lead (piano M2 Task 18): soltanto con la funzione `manage_tags` (la testata lo sa dal server). Si sceglie fra
// i tag che ci sono già (`GET /api/tags`) e il server riceve l'insieme intero (`PUT …/tags`). Con `canCreate` (24/10/2026) la
// casella cerca o crea: un nome che non c'è propone «Crea il tag «…»», che crea il tag (`POST /api/tags`) e salva subito i tag
// scelti con quello nuovo. Un tag nascosto già sul Lead resta nell'elenco, scelto. Senza differenze non parte nulla.

/** Oltre questo numero di tag compare la casella per cercarli. */
const FILTER_FROM = 8;

type Props = {
  tab: TabContext;
  lead: CardLead;
  onSubmit: (tagIds: string[]) => Promise<LeadWriteResponse>;
  onClose: () => void;
  /** Se si possono creare tag nuovi (`manage_tags`): la casella diventa «Cerca o crea un tag». */
  canCreate?: boolean;
};

export function TagsDialog({ tab, lead, onSubmit, onClose, canCreate = false }: Props) {
  const t = useT();
  const toast = useToast();
  const formId = useId();
  const catalog = useQuery(tagsQuery(tab));
  const createTag = useCreateTag(tab);
  const [selected, setSelected] = useState<string[]>(() => lead.tags.map((tag) => tag.id));
  const [filter, setFilter] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const filterRef = useRef<HTMLInputElement>(null);

  // Il catalogo e, in fondo, i tag del Lead che il catalogo non mostra più (nascosti).
  const known = catalog.data ?? [];
  const all = [...known, ...lead.tags.filter((tag) => !known.some((candidate) => candidate.id === tag.id))];
  // Senza maiuscole, accenti e segni, come il confronto dei doppioni: un tag che c'è già resta sempre in vista.
  const q = filter.trim();
  const visible = tagsMatching(all, filter);
  const candidate = canCreate && catalog.isSuccess ? newTagName(filter, all) : null;

  function toggle(id: string, checked: boolean) {
    setSelected((current) => (checked ? [...current.filter((candidate) => candidate !== id), id] : current.filter((candidate) => candidate !== id)));
  }

  function failed(failure: unknown) {
    setPending(false);
    const conflict = (failure instanceof ApiRequestError && failure.code === "version_conflict") || failure instanceof SupersededWriteError;
    setError(conflict ? t("leadCard.conflict") : messageForError(t, failure));
  }

  // «Crea il tag «…»»: il tag nuovo (o quello nato nel frattempo con lo stesso nome) si sceglie e i tag si salvano subito. Se il
  // salvataggio non riesce, il tag resta creato e scelto: «Salva i tag» riprova, e il focus torna sulla casella.
  async function handleCreate(name: string) {
    if (pending) return;
    setPending(true);
    setError(null);
    try {
      const { tag, created } = await createTag.mutateAsync(name);
      const next = [...selected.filter((id) => id !== tag.id), tag.id];
      setSelected(next);
      setFilter("");
      await onSubmit(next);
      onClose();
      toast.show(created ? t("leadCard.tagsDialog.created", { name: tag.name }) : t("leadCard.tagsDialog.saved"), "success");
    } catch (failure) {
      failed(failure);
      filterRef.current?.focus();
    }
  }

  // Invio nella casella non salva la finestra: crea il tag soltanto se nessun tag che c'è risponde a ciò che è scritto.
  function handleFilterKey(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key !== "Enter") return;
    event.preventDefault();
    if (candidate !== null && visible.length === 0) void handleCreate(candidate);
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (pending) return;
    const before = new Set(lead.tags.map((tag) => tag.id));
    if (selected.length === before.size && selected.every((id) => before.has(id))) {
      onClose();
      return;
    }
    setPending(true);
    setError(null);
    try {
      await onSubmit(selected);
      onClose();
      toast.show(t("leadCard.tagsDialog.saved"), "success");
    } catch (failure) {
      failed(failure);
    }
  }

  return (
    <Dialog
      open
      title={t("leadCard.tagsDialog.title")}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>{t("common.cancel")}</Button>
          <Button variant="primary" type="submit" form={formId} disabled={pending} aria-busy={pending ? "true" : undefined}>
            {t("leadCard.tagsDialog.submit")}
          </Button>
        </>
      }
    >
      <form id={formId} className="g-stack" onSubmit={(event) => void handleSubmit(event)} aria-busy={pending} noValidate>
        <p className="g-field__hint">{t(canCreate ? "leadCard.tagsDialog.hintCreate" : "leadCard.tagsDialog.hint")}</p>
        {canCreate || all.length > FILTER_FROM ? (
          <div className="g-field">
            <TextField
              ref={filterRef}
              id={`${formId}-filter`}
              label={t(canCreate ? "leadCard.tagsDialog.filterOrCreate" : "leadCard.tagsDialog.filter")}
              type="search"
              autoComplete="off"
              maxLength={canCreate ? TAG_NAME_MAX : undefined}
              value={filter}
              onChange={(event) => setFilter(event.target.value)}
              onKeyDown={handleFilterKey}
            />
            {/* La voce compare mentre si scrive: la regione la annuncia. */}
            {canCreate ? (
              <div aria-live="polite">
                {candidate !== null ? (
                  <Button onClick={() => void handleCreate(candidate)} disabled={pending}>
                    {t("leadCard.tagsDialog.create", { name: candidate })}
                  </Button>
                ) : null}
              </div>
            ) : null}
          </div>
        ) : null}
        {catalog.isPending ? <p className="g-field__hint">{t("common.loading")}</p> : null}
        {catalog.isError ? (
          <p className="g-field__error" role="alert">
            {messageForError(t, catalog.error)}
          </p>
        ) : null}
        {catalog.isSuccess && all.length === 0 ? <p className="g-field__hint">{t("leadCard.tagsDialog.empty")}</p> : null}
        {q && visible.length === 0 ? <p className="g-field__hint">{t("leadCard.tagsDialog.none", { q: filter.trim() })}</p> : null}
        <div className="g-lead-tags-dialog__list">
          {visible.map((tag) => (
            <Checkbox key={tag.id} id={`${formId}-${tag.id}`} label={tag.name} checked={selected.includes(tag.id)} onChange={(checked) => toggle(tag.id, checked)} />
          ))}
        </div>
        {error ? (
          <p className="g-field__error" role="alert">
            {error}
          </p>
        ) : null}
      </form>
    </Dialog>
  );
}
